import type { ForecastChange, InstanceActivityEvent, InstanceChangeDays, InstanceDetail, InstanceSnapshot, ResolvedSection } from "@keydials/api-client";

/** KEY-890: which way the second dial looks — back to Then, or ahead to how it will be if nothing is done. */
export type CompareDirection = "back" | "ahead";

/**
 * Look back as a compare mode: the pure half — dates, the Then detail, and which changes a card shows.
 * Kept free of React so the rules that decide what the two dials and the Changes tab say can be tested
 * without mounting a workspace.
 */

/** The preset pills on the range bar, in order. "Start of term" waits for dials to know what a term is. */
export const COMPARE_PRESETS: { id: string; label: string; days: number }[] = [
  { id: "week", label: "Last week", days: 7 },
  { id: "month", label: "Last month", days: 30 },
  { id: "quarter", label: "3 months", days: 91 },
  { id: "year", label: "Last year", days: 365 },
];

/** KEY-890: Look ahead's pills — tomorrow first, as the near future is what people plan. */
export const AHEAD_PRESETS: { id: string; label: string; days: number }[] = [
  { id: "tomorrow", label: "Tomorrow", days: 1 },
  { id: "week", label: "Next week", days: 7 },
  { id: "month", label: "Next month", days: 30 },
  { id: "quarter", label: "3 months", days: 91 },
];

/** Where Look ahead opens the first time: next week. */
export const DEFAULT_AHEAD_DAYS = 7;

/** The furthest ahead the server looks. */
export const MAX_AHEAD_DAYS = 365;

/** What Then opens on the first time, before anyone has picked: the "3 months" pill, the design's default. */
export const DEFAULT_PRESET_DAYS = 91;

/** A local calendar day as `yyyy-mm-dd`, the `<input type="date">` form. */
export function toInputDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function daysAhead(days: number, now: Date = new Date()): string {
  return daysAgo(-days, now);
}

export function daysAgo(days: number, now: Date = new Date()): string {
  const d = new Date(now);
  d.setDate(d.getDate() - days);
  return toInputDate(d);
}

/** "As it was on the 6th" means by the END of the 6th, in the reader's own zone. */
export function endOfDay(day: string): Date {
  return new Date(`${day}T23:59:59`);
}

/** "29 Jun 2026" — the chip, the button and the aria-labels all say the date this one way. */
export function fmtCompareDate(day: string): string {
  const d = new Date(`${day}T12:00:00`);
  if (Number.isNaN(d.getTime())) return day;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/**
 * The past as a detail: today's dial with the replayed statuses, review dates, section statuses, field
 * values and rollup swapped in, so it goes through the same `buildDialVM` as the live dial and the two faces
 * are drawn by one rule. A server too old to send section statuses or fields leaves today's in place.
 */
export function thenDetail(detail: InstanceDetail, snapshot: InstanceSnapshot, days?: ResolvedSection[] | null): InstanceDetail {
  const stamp = { updatedAtUtc: snapshot.atUtc, updatedByEmail: "" };
  return {
    ...detail,
    // KEY-890: a days dial looked at ahead shows the days from that date — its day sections, in place of today's.
    sections: days?.length ? withDays(detail.sections, days) : detail.sections,
    statuses: snapshot.statuses.map((s) => ({ ...s, reviewDate: s.reviewDate ?? null, ...stamp })),
    itemStatuses: snapshot.itemStatuses.map((s) => ({ ...s, reviewDate: s.reviewDate ?? null, ...stamp })),
    rollup: snapshot.rollup,
    sectionStatuses: snapshot.sectionStatuses == null
      ? detail.sectionStatuses
      : snapshot.sectionStatuses.map((s) => ({ ...s, ...stamp })),
    // KEY-795: section colours as they were then; an older server sends none, so today's stay.
    sectionColours: snapshot.sectionColours === undefined ? detail.sectionColours : snapshot.sectionColours,
    nodeAttributeValues: snapshot.fields == null
      ? detail.nodeAttributeValues
      : snapshot.fields
        .filter((f) => f.value != null)
        .map((f) => ({ ...f, value: f.value!, ...stamp })),
  };
}

/** Everything that happened after the chosen day, newest first as the feed already orders it. */
export function changesSince(events: InstanceActivityEvent[], day: string): InstanceActivityEvent[] {
  const since = endOfDay(day).getTime();
  return events.filter((e) => new Date(e.occurredAtUtc).getTime() > since);
}

export const isStatusChange = (e: InstanceActivityEvent) =>
  e.kind === "ItemStatus" || e.kind === "SegmentStatus" || e.kind === "SectionStatus";

/** "4 changes, 2 to a status" — the Changes tab's count line. */
export function changesSummary(events: InstanceActivityEvent[]): string {
  if (events.length === 0) return "Nothing changed";
  const statuses = events.filter(isStatusChange).length;
  return `${events.length} change${events.length === 1 ? "" : "s"}${statuses > 0 ? `, ${statuses} to a status` : ""}`;
}

/** Why the Look back button is greyed out on a group dial. Said in full in its tooltip. */
export const GROUP_DIAL_REASON =
  "Look back isn't available on group dials yet. Group rollups aren't recorded day by day, so there's nothing to turn the clock back to. Open a subdial to compare it.";

/**
 * KEY-760: what Look back says on a dial with no history yet — a brand-new dial, or one nobody has touched.
 * Then would only be Now again, so both the tab and the compare bar say this instead of offering a past to pick.
 */
export const NOTHING_TO_LOOK_BACK_ON = "Nothing to look back on yet — changes you make from now on will show here.";

/**
 * True only once it is KNOWN that the reader can see no change on this dial: the timeline came back with no
 * change days. Unknown (still loading, or the ticks couldn't be had) is not empty — the controls stay usable.
 */
export function noHistoryYet(changeDays: InstanceChangeDays | null | undefined): boolean {
  return !!changeDays && changeDays.days.length === 0;
}

/** The tab's form of {@link noHistoryYet}: the whole activity feed, once loaded, holds nothing. */
export function noActivityYet(events: InstanceActivityEvent[] | null | undefined): boolean {
  return !!events && events.length === 0;
}

/**
 * Then, on a dial with no history yet, IS Now: nothing has changed for it to have been anything else. Built from
 * the live detail, so compare mode on a brand-new dial shows the two faces alike and a card with no differences
 * without waiting on — or failing with — a replay that has nothing to replay.
 */
export function snapshotOfNow(detail: InstanceDetail, now: Date = new Date()): InstanceSnapshot {
  return {
    atUtc: now.toISOString(),
    statuses: detail.statuses.map((s) => ({ sectionKey: s.sectionKey, segmentKey: s.segmentKey, statusKey: s.statusKey, reviewDate: s.reviewDate ?? null })),
    itemStatuses: (detail.itemStatuses ?? []).map((s) => ({
      sectionKey: s.sectionKey, segmentKey: s.segmentKey, itemKey: s.itemKey, dimensionKey: s.dimensionKey, statusKey: s.statusKey, reviewDate: s.reviewDate ?? null,
    })),
    rollup: detail.rollup ?? null,
    sectionStatuses: detail.sectionStatuses == null ? null : detail.sectionStatuses.map((s) => ({ sectionKey: s.sectionKey, statusKey: s.statusKey })),
    fields: (detail.nodeAttributeValues ?? []).map((v) => ({
      nodeType: v.nodeType, sectionKey: v.sectionKey, segmentKey: v.segmentKey, itemKey: v.itemKey, attributeKey: v.attributeKey, value: v.value,
    })),
  };
}

/** Today's day sections swapped, in order, for another week's — every other section stays where it is. */
export function withDays(sections: ResolvedSection[], days: ResolvedSection[]): ResolvedSection[] {
  let next = 0;
  const swapped: ResolvedSection[] = [];
  for (const s of sections) {
    if (s.kind === "Days") {
      if (next < days.length) swapped.push(days[next++]);
    } else {
      swapped.push(s);
    }
  }
  return swapped;
}

/** KEY-890: "Due date", "Changes back", "Reset" — why a forecast change happens, as the list says it. */
export function forecastReason(reason: ForecastChange["reason"]): string {
  return reason === "dueDate" ? "its due date" : reason === "changeBack" ? "set to change back" : "scheduled reset";
}

/** KEY-890: the forecast changes for what the card is showing — the whole dial, a section, or one action point. */
export function changesIn(changes: ForecastChange[], scope?: { sectionKey: string; segmentKey?: string | null }): ForecastChange[] {
  if (!scope) return changes;
  return changes.filter((c) => c.sectionKey === scope.sectionKey && (!scope.segmentKey || c.segmentKey === scope.segmentKey));
}
